import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ButtonBase, IconButton, Typography, useTheme } from '@mui/material';
import { useSetAtom } from 'jotai';
import { FC, useMemo, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { SourcingSavedSearch } from '../../../../../schemas/SourcingList.schema';
import { useToastMessageState } from '../../../../ToastMessage/ToastMessageProvider';
import { useContextMenu } from '../../../../ContextMenu/useContextMenu';
import { SourcingMenu } from '../../../../../pages/Sourcing/components/SourcingMenu';
import { buildListMenuSections } from '../../../../../pages/Sourcing/util/buildListMenuSections';
import { useRefreshListNow } from '../../../../../pages/Sourcing/hooks/useSourcingListActions';
import { refreshSettingsDialogAtom } from '../../../../../pages/Sourcing/state/SourcingListsState';
import {
  ItemInner,
  ItemLeft,
  ItemRow,
  SavedSearchCompanyIcon,
  SavedSearchPersonIcon,
} from './SourcingNavItemShared';

export interface SavedSearchItemProps {
  item: SourcingSavedSearch;
}

export const SavedSearchItem: FC<SavedSearchItemProps> = ({ item }) => {
  const { secondary } = useTheme().colors;
  const { pushErrorToast } = useToastMessageState();
  const match = useMatch(`/${ROUTES.SOURCING}/${ROUTES.SAVED_SEARCHES}/${item.id}`);
  const isSelected = match !== null;
  const { menuPos, handleMenuButtonClick, handleContextMenu, handleClose } = useContextMenu();
  const [isHovered, setIsHovered] = useState(false);

  const showActions = isHovered || isSelected || Boolean(menuPos);
  const openRefreshSettings = useSetAtom(refreshSettingsDialogAtom);
  const { isPending: isRunLoading, mutate: refreshList } = useRefreshListNow();

  const menuSections = useMemo(
    () =>
      buildListMenuSections({
        list: item,
        isDuplicating: false,
        isEnrichLoading: false,
        isRunLoading,
        onRename: () => {},
        onDuplicate: () => {},
        onDelete: () => {},
        onEnrichNow: () => {},
        onRunNow: () => refreshList(item),
        onOpenRefreshSettings: () => openRefreshSettings(item),
        onCopyLinkError: (message) => pushErrorToast({ message }),
      }),
    [item, pushErrorToast, openRefreshSettings, isRunLoading, refreshList]
  );

  return (
    <>
      <ButtonBase
        TouchRippleProps={{ color: 'white' }}
        style={{ width: '100%', display: 'block' }}
        component={Link}
        to={`/${ROUTES.SOURCING}/${ROUTES.SAVED_SEARCHES}/${item.id}`}
      >
        <ItemRow
          isSelected={isSelected}
          isHovered={isHovered}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onContextMenu={handleContextMenu}
        >
          <ItemInner>
            <ItemLeft>
              {item.entityType === 'person' ? (
                <SavedSearchPersonIcon color={secondary[20]} />
              ) : (
                <SavedSearchCompanyIcon color={secondary[20]} />
              )}
              <Typography
                variant='body2'
                color={secondary[10]}
                noWrap
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {item.name}
              </Typography>
            </ItemLeft>
            {showActions && (
              <IconButton
                size='small'
                onClick={handleMenuButtonClick}
                sx={{ color: secondary[10], p: '2px', flexShrink: 0 }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </ItemInner>
        </ItemRow>
      </ButtonBase>
      <SourcingMenu sections={menuSections} menuPos={menuPos} onClose={handleClose} />
    </>
  );
};
